import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  GetSystemValidationRulesBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import SystemValidationRulesModal from './SystemValidationRulesModal';

import global from "../../../../../global.scss";

const SystemValidationRulesPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [svModalOpen, setSvModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [rulesList, setRulesList] = useState([]);
  const [rulesTotal, setRulesTotal] = useState(0);

  const [rulesMenuOpen, setRulesMenuOpen] = useState(false);
  const [rulesMenuAnchor, setRulesMenuAnchor] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "Validation name", sort: "validation_name" },
    { label: "Trigger condition", sort: "trigger_condition" },
    { label: "Missing variable", sort: "missing_variable" },
    { label: "Is active", sort: "is_active" },
    { label: "Time stamps", sort: "created_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetRules();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setRulesList(state.systemValidationRulesList.records || []);
      setRulesTotal(state.systemValidationRulesList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetRules(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetSystemValidationRulesBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  return (
    <Box>

      <SystemValidationRulesModal
        open={svModalOpen}
        setOpen={setSvModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetRules}
      />

      <TopBar
        title={"System Validation Rules"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        button1Text={"Add rule"}
        button1OnClick={() => {
          setUpdate({ defaultMode: 1 });
          setModalTitle("Add system validation rule");
          setSvModalOpen(!svModalOpen);
        }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          rulesList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No system validation rule found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                rulesList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>{row.validation_name || <NA />}</TableCell>
                    <TableCell sx={{ maxWidth: "200px" }}>
                      <Typography
                        variant='body2'
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: "200px"
                        }}
                        title={row.trigger_condition}
                      >
                        {row.trigger_condition || <NA />}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.missing_variable || <NA />}</TableCell>
                    <TableCell>
                      {
                        row.is_active === true
                        ? <Chip size="small" label="Yes" sx={{ backgroundColor: "rgba(0,200,0,0.15)", color: "green" }} />
                        : row.is_active === false
                          ? <Chip size="small" label="No" sx={{ backgroundColor: "rgba(200,0,0,0.15)", color: "red" }} />
                          : <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setRulesMenuAnchor(e.currentTarget);
                          setRulesMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.validation_name || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={10}
                  count={rulesTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={rulesMenuOpen}
        anchorEl={rulesMenuAnchor}
        onClose={() => setRulesMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit " + modalTitle);
            setUpdate({ ...update, defaultMode: 2 });
            setRulesMenuOpen(false);
            setSvModalOpen(!svModalOpen);
          }}>
          <Box className="flexCenter_Row" gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default SystemValidationRulesPage;
